import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  PLAN_APP_LABEL,
  PLAN_PERIOD_LABEL,
  type CreatePlanInput,
  type Plan,
  type PlanApp,
  type PlanPeriod,
  type UpdatePlanInput,
} from "@/features/plans/schemas/plan";

const PLANS_COLLECTION = "plans";

function getPlansCollection(): CollectionReference<DocumentData> {
  return collection(db, PLANS_COLLECTION);
}

function mapPeriod(value: unknown): PlanPeriod {
  if (value === "month" || value === "annual" || value === "once") {
    return value;
  }
  return "annual";
}

function mapApp(value: unknown): PlanApp | undefined {
  if (value === "patient" || value === "physician" || value === "institution") {
    return value;
  }
  return undefined;
}

function mapBenefits(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function assertPlanApp(app: PlanApp | undefined): PlanApp {
  if (!app || !mapApp(app)) {
    throw new Error("La aplicación del plan es obligatoria");
  }
  return app;
}

function assertPlanFields(input: {
  name: string;
  price: number;
}): { name: string; price: number } {
  const name = input.name.trim();

  if (!name) {
    throw new Error("El nombre del plan es obligatorio");
  }
  if (!Number.isFinite(input.price) || input.price < 0) {
    throw new Error("El precio del plan es obligatorio y debe ser válido");
  }

  return { name, price: input.price };
}

async function assertUniquePlan(
  name: string,
  app: PlanApp,
  period: PlanPeriod,
  excludeDocId?: string
): Promise<void> {
  const existing = await listPlans();
  const clash = existing.find(
    (plan) =>
      plan.app === app &&
      plan.period === period &&
      plan.name.toLowerCase() === name.toLowerCase() &&
      plan.docId !== excludeDocId
  );
  if (clash) {
    throw new Error(
      `Ya existe un plan "${name}" (${PLAN_PERIOD_LABEL[period]}) para ${PLAN_APP_LABEL[app]}`
    );
  }
}

/**
 * Lista los planes desde Firestore.
 */
export async function listPlans(): Promise<Plan[]> {
  const snapshot = await getDocs(getPlansCollection());

  const items: Plan[] = snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const app = mapApp(data.app);
    return {
      docId: docSnap.id,
      name: typeof data.name === "string" ? data.name : "",
      period: mapPeriod(data.period),
      price: typeof data.price === "number" ? data.price : 0,
      benefits: mapBenefits(data.benefits),
      ...(app ? { app } : {}),
      stripeId: typeof data.stripeId === "string" ? data.stripeId : undefined,
      stripePriceId:
        typeof data.stripePriceId === "string"
          ? data.stripePriceId
          : undefined,
      venflowId:
        typeof data.venflowId === "string" ? data.venflowId : undefined,
    };
  });

  return items
    .filter((plan) => plan.name.length > 0)
    .sort((a, b) => {
      const appA = a.app ? PLAN_APP_LABEL[a.app] : "Sin app";
      const appB = b.app ? PLAN_APP_LABEL[b.app] : "Sin app";
      const byApp = appA.localeCompare(appB, "es");
      if (byApp !== 0) return byApp;
      const byName = a.name.localeCompare(b.name, "es");
      if (byName !== 0) return byName;
      return a.price - b.price;
    });
}

/**
 * Crea un plan en `plans` (doc id auto).
 */
export async function createPlan(input: CreatePlanInput): Promise<void> {
  const fields = assertPlanFields(input);
  const app = assertPlanApp(input.app);
  await assertUniquePlan(fields.name, app, input.period);

  const payload = {
    name: fields.name,
    period: input.period,
    price: fields.price,
    benefits: input.benefits,
    app,
    stripeId: input.stripeId?.trim() || null,
    stripePriceId: input.stripePriceId?.trim() || null,
    venflowId: input.venflowId?.trim() || null,
  };

  await addDoc(getPlansCollection(), payload);
}

/**
 * Actualiza un plan existente por document id.
 */
export async function updatePlan(
  docId: string,
  input: UpdatePlanInput
): Promise<void> {
  const ref = doc(db, PLANS_COLLECTION, docId);
  const payload: Record<string, unknown> = {};

  const existingPlans = await listPlans();
  const current = existingPlans.find((plan) => plan.docId === docId);

  if (input.name !== undefined) {
    const name = input.name.trim();
    if (!name) {
      throw new Error("El nombre del plan es obligatorio");
    }
    payload.name = name;
  }
  if (input.app !== undefined) {
    payload.app = assertPlanApp(input.app);
  }
  if (input.period !== undefined) {
    payload.period = input.period;
  }
  if (input.price !== undefined) {
    if (!Number.isFinite(input.price) || input.price < 0) {
      throw new Error("El precio del plan debe ser válido");
    }
    payload.price = input.price;
  }
  if (input.benefits !== undefined) {
    payload.benefits = input.benefits;
  }
  if (input.stripeId !== undefined) {
    payload.stripeId = input.stripeId.trim() || null;
  }
  if (input.stripePriceId !== undefined) {
    payload.stripePriceId = input.stripePriceId.trim() || null;
  }
  if (input.venflowId !== undefined) {
    payload.venflowId = input.venflowId.trim() || null;
  }

  const nextName =
    typeof payload.name === "string" ? payload.name : current?.name;
  const nextApp =
    (payload.app as PlanApp | undefined) ?? current?.app;
  const nextPeriod =
    (payload.period as PlanPeriod | undefined) ?? current?.period;

  if (
    nextName &&
    nextApp &&
    nextPeriod &&
    (input.name !== undefined ||
      input.app !== undefined ||
      input.period !== undefined)
  ) {
    await assertUniquePlan(nextName, nextApp, nextPeriod, docId);
  }

  if (Object.keys(payload).length === 0) return;
  await updateDoc(ref, payload);
}

/**
 * Elimina un plan por document id.
 */
export async function deletePlan(docId: string): Promise<void> {
  await deleteDoc(doc(db, PLANS_COLLECTION, docId));
}

/** Resuelve un plan asignado por docId (nuevo) o por nombre (legacy). */
export function resolveAssignedPlan(
  planId: string | null | undefined,
  plans: Plan[]
): Plan | undefined {
  if (!planId) return undefined;

  const byDocId = plans.find((plan) => plan.docId === planId);
  if (byDocId) return byDocId;

  const byName = plans.filter((plan) => plan.name === planId);
  return byName.length === 1 ? byName[0] : undefined;
}
